// C-S5A2-1 «Подсказки на основе анализов» — Stage 4 §6.
// Source: docs/specs/sprint-5a.2/stage-4-frontend.md §5 (state machine) + §6 (полный код).
//
// Самостоятельно дёргает useBiomarkerSummary(), без props. Рендерится ТОЛЬКО внутри HomeConfirmed.

import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, Star } from 'lucide-react';
import { useBiomarkerSummary } from '@/api/hooks/biomarkerInsights';
import type {
  BiomarkerInsightItem,
  BiomarkerSummaryReady,
  BiomarkerSummaryResponse,
} from '@/api/types';

// Stage 1 §4.6 + Cfg-S5A2-1 + R1 L/M fix — hardcoded 60_000 ms для Stage 4.
const POLL_TIMEOUT_MS = 60_000;

export type SummaryCardState =
  | 'loading'
  | 'loaded'
  | 'refreshing'
  | 'unavailable'
  | 'hide_disabled'
  | 'hide_no_uploads'
  | 'terminal_no_actionable'
  | 'terminal_no_verified_rule';

type DeriveSummaryStateInput = {
  isLoading: boolean;
  isError: boolean;
  data: BiomarkerSummaryResponse | undefined;
  hasStash: boolean;
  isTimedOut: boolean;
};

export function deriveSummaryState(input: DeriveSummaryStateInput): SummaryCardState {
  const { isLoading, isError, data, hasStash, isTimedOut } = input;

  if (data?.status === 'disabled') return 'hide_disabled';
  if (data?.status === 'no_uploads') return 'hide_no_uploads';
  if (data?.status === 'no_actionable_biomarkers') return 'terminal_no_actionable';
  if (data?.status === 'no_verified_rule') return 'terminal_no_verified_rule';

  if (isTimedOut && hasStash) return 'loaded';
  if (isError && hasStash) return 'loaded';

  if (isTimedOut) return 'unavailable';
  if (isError) return 'unavailable';

  if (data?.status === 'ready') return 'loaded';
  if (data?.status === 'generating' && hasStash) return 'refreshing';
  if (data?.status === 'generating' && !hasStash) return 'loading';

  if (isLoading) return 'loading';

  return 'loading';
}

export function renderSummaryInsights(
  state: SummaryCardState,
  data: BiomarkerSummaryResponse | undefined,
  stash: BiomarkerSummaryReady | null,
): BiomarkerInsightItem[] {
  if (state === 'loaded') {
    if (data?.status === 'ready') return data.insights;
    if (stash !== null) return stash.insights;
  }
  if (state === 'refreshing' && stash !== null) return stash.insights;
  return [];
}

export function pickSummaryDisclaimer(
  state: SummaryCardState,
  data: BiomarkerSummaryResponse | undefined,
  stash: BiomarkerSummaryReady | null,
): string {
  if (state === 'loaded') {
    if (data?.status === 'ready') return data.disclaimer;
    if (stash !== null) return stash.disclaimer;
  }
  if (state === 'refreshing' && stash !== null) return stash.disclaimer;
  return '';
}

export function pickSummaryPermanentDisclaimer(
  state: SummaryCardState,
  data: BiomarkerSummaryResponse | undefined,
  stash: BiomarkerSummaryReady | null,
): string {
  if (state === 'loaded' || state === 'refreshing') {
    if (data && 'permanent_disclaimer' in data) return data.permanent_disclaimer;
    if (stash !== null) return stash.permanent_disclaimer;
  }
  if (state === 'unavailable' && stash !== null) return stash.permanent_disclaimer;
  if (state === 'terminal_no_actionable' || state === 'terminal_no_verified_rule') {
    if (data && 'permanent_disclaimer' in data) return data.permanent_disclaimer;
  }
  return '';
}

export function BiomarkerSummaryCard() {
  const generatingStartedAt = useRef<number | null>(null);
  const [isTimedOut, setIsTimedOut] = useState(false);

  const query = useBiomarkerSummary({ enabled: !isTimedOut });

  const lastReadyRef = useRef<BiomarkerSummaryReady | null>(null);
  useEffect(() => {
    if (query.data?.status === 'ready') {
      lastReadyRef.current = query.data;
    }
  }, [query.data]);

  useEffect(() => {
    const status = query.data?.status;
    if (status === 'ready' || query.isError || (status !== undefined && status !== 'generating')) {
      generatingStartedAt.current = null;
      setIsTimedOut(false);
      return;
    }
    if (status === 'generating') {
      if (generatingStartedAt.current === null) {
        generatingStartedAt.current = Date.now();
      }
      const elapsed = Date.now() - generatingStartedAt.current;
      const remaining = Math.max(0, POLL_TIMEOUT_MS - elapsed);
      if (remaining === 0) {
        setIsTimedOut(true);
        return;
      }
      const t = setTimeout(() => setIsTimedOut(true), remaining);
      return () => clearTimeout(t);
    }
  }, [query.data?.status, query.isError]);

  const state = deriveSummaryState({
    isLoading: query.isLoading,
    isError: query.isError,
    data: query.data,
    hasStash: lastReadyRef.current !== null,
    isTimedOut,
  });

  if (state === 'hide_disabled' || state === 'hide_no_uploads') {
    return null;
  }

  const insights = renderSummaryInsights(state, query.data, lastReadyRef.current);
  const blockDisclaimer = pickSummaryDisclaimer(state, query.data, lastReadyRef.current);
  const permanentDisclaimer = pickSummaryPermanentDisclaimer(state, query.data, lastReadyRef.current);

  if (state === 'terminal_no_verified_rule') {
    return (
      <div className="px-4 pt-2">
        {permanentDisclaimer && (
          <p className="text-hl-small leading-relaxed text-muted-foreground/80">
            {permanentDisclaimer}
          </p>
        )}
      </div>
    );
  }

  // Variant B: terminal_no_actionable → тонкая positive строка вместо полной карточки.
  // Wellness ниже всё равно даст полезный контент — biomarker-блок схлопывается.
  if (state === 'terminal_no_actionable') {
    return (
      <div className="px-4 pt-2">
        <div className="flex items-center gap-2 rounded-surface border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-primary-text">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>Все биомаркеры в норме</span>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pt-2">
      <section
        className="rounded-surface border border-primary/20 bg-primary/5 p-5 transition-opacity duration-300"
        aria-live="polite"
        aria-busy={state === 'loading' || state === 'refreshing'}
      >
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-control bg-primary text-primary-foreground">
            <Star className="h-4 w-4 fill-current" aria-hidden="true" />
          </div>
          <h2 className="text-sm font-semibold text-primary-text">Подсказки по анализам</h2>
        </div>

        {state === 'refreshing' && (
          <div
            className="mb-3 flex items-center gap-2 rounded-md bg-primary/5 px-3 py-2 text-hl-small text-primary-text"
            role="status"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            <span>Обновляем подсказки на основе новых анализов…</span>
          </div>
        )}

        {state === 'loading' && (
          <div className="space-y-2.5" aria-hidden="true">
            <div className="h-4 w-full animate-pulse rounded bg-primary/30" />
            <div className="h-4 w-[85%] animate-pulse rounded bg-primary/30" />
            <div className="h-4 w-[70%] animate-pulse rounded bg-primary/30" />
          </div>
        )}

        {(state === 'loaded' || state === 'refreshing') && (
          <>
            <ul role="list" className="mb-3 space-y-2.5">
              {insights.map((insight) => (
                <li
                  key={insight.id}
                  className="flex items-start gap-2.5 text-hl-secondary leading-snug text-foreground"
                >
                  <span className="mt-0.5 text-primary-text" aria-hidden="true">•</span>
                  <span>{insight.text}</span>
                </li>
              ))}
            </ul>
            {blockDisclaimer && (
              <p className="mt-4 text-hl-small leading-relaxed text-muted-foreground">
                {blockDisclaimer}
              </p>
            )}
            {permanentDisclaimer && (
              <p className="mt-2 text-hl-small leading-relaxed text-muted-foreground/80">
                {permanentDisclaimer}
              </p>
            )}
          </>
        )}

        {state === 'unavailable' && (
          <>
            <div className="py-2 text-center text-sm text-muted-foreground">
              <p>Подсказки временно недоступны.</p>
              <p className="mt-1">Откройте экран позже — мы их добавим.</p>
            </div>
            {permanentDisclaimer && (
              <p className="mt-2 text-hl-small leading-relaxed text-muted-foreground/80">
                {permanentDisclaimer}
              </p>
            )}
          </>
        )}
      </section>
    </div>
  );
}
